import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "../../../components/layout/DashboardLayout";
import AceAvatar from "../../../components/shared/AceAvatar";

import {
  CONTENT_CATEGORIES,
  CONTENT_STATUS,
  fetchChallengeById,
  updateChallenge,
} from "../../../services/contentService";

function StatusPill({ status }) {
  const map = {
    [CONTENT_STATUS.PUBLISHED]: "is-published",
    [CONTENT_STATUS.REVIEW]: "is-review",
    [CONTENT_STATUS.DRAFT]: "is-draft",
  };

  return (
    <span className={`ac-content-statuspill ${map[status] || ""}`}>
      {status}
    </span>
  );
}

const DURATION_OPTIONS = ["1 min", "2 min", "5 min", "10 min", "15 min", "20 min", "25 min", "30 min"];

export default function ChallengeDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: "",
    category: "Fokus",
    duration: "5 min",
    status: CONTENT_STATUS.DRAFT,
    description: "",
  });

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const data = await fetchChallengeById(id);

        if (!mounted) return;
        setChallenge(data);

        if (data) {
          setForm({
            title: data.title || "",
            category: data.category || "Fokus",
            duration: data.duration || "5 min",
            status: data.status || CONTENT_STATUS.DRAFT,
            description: data.description || "",
          });
        }
      } catch (error) {
        console.error("Challenge konnte nicht geladen werden:", error);
        if (!mounted) return;
        setChallenge(null);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [id]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  async function handleSave() {
    try {
      setSaving(true);
      await updateChallenge(id, form);
      navigate("/content/challenges");
    } catch (error) {
      console.error("Challenge konnte nicht gespeichert werden:", error);
      alert("Änderungen konnten nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <DashboardLayout title="Inhalte · Challenge Details">
        <div className="ac-soft-panel ac-content-block">
          <div className="text-lg font-extrabold text-ac-text">Challenge wird geladen...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!challenge) {
    return (
      <DashboardLayout title="Inhalte · Challenge">
        <div className="ac-soft-panel ac-content-block">
          <div className="text-lg font-extrabold text-ac-text">Challenge nicht gefunden</div>
          <button
            onClick={() => navigate("/content/challenges")}
            className="mt-4 ac-ghostbtn"
          >
            Zurück zur Liste
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Inhalte · Challenge Details">
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xl font-extrabold text-ac-text">Challenge Details</div>

          <button onClick={() => navigate("/content/challenges")} className="ac-ghostbtn">
            ← Challenges
          </button>
        </div>

        <section className="ac-soft-panel ac-content-hero">
          <div className="ac-content-hero-grid">
            <div>
              <h2 className="ac-content-hero-title">
                {form.title || "Challenge Details"}
              </h2>
              <p className="ac-content-hero-text">
                Bearbeite Titel, Dauer, Beschreibung und Freigabestatus direkt hier.
              </p>
            </div>

            <div className="ac-content-hero-side">
              <div className="ac-content-acebox">
                <AceAvatar variant="motiviert2" size={56} />
                <div className="ac-content-acebubble">
                  Ace sagt: Gute Challenges sind klar, kurz und leicht durchziehbar.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ac-soft-panel ac-content-block">
          <div className="ac-content-detail-meta">
            <div className="ac-content-detail-meta-card">
              <div className="ac-content-label">Status</div>
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <StatusPill status={form.status} />
                <select
                  value={form.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                  className="ac-select"
                >
                  <option value={CONTENT_STATUS.DRAFT}>{CONTENT_STATUS.DRAFT}</option>
                  <option value={CONTENT_STATUS.REVIEW}>{CONTENT_STATUS.REVIEW}</option>
                  <option value={CONTENT_STATUS.PUBLISHED}>{CONTENT_STATUS.PUBLISHED}</option>
                </select>
              </div>
              <div className="mt-3 text-xs font-bold text-ac-muted">
                Teilnehmer aktuell: {challenge.participants}
              </div>
            </div>

            <div className="ac-content-detail-meta-card ac-content-detail-meta-wide">
              <div className="ac-content-detail-grid ac-content-detail-grid-4">
                <div>
                  <label className="ac-content-label">Kategorie</label>
                  <select
                    value={form.category}
                    onChange={(e) => handleChange("category", e.target.value)}
                    className="ac-select mt-2 w-full"
                  >
                    {CONTENT_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="ac-content-label">Dauer</label>
                  <select
                    value={form.duration}
                    onChange={(e) => handleChange("duration", e.target.value)}
                    className="ac-select mt-2 w-full"
                  >
                    {DURATION_OPTIONS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="ac-content-label">Zuletzt bearbeitet von</label>
                  <div className="ac-content-staticbox mt-2">{challenge.author}</div>
                </div>

                <div>
                  <label className="ac-content-label">Zuletzt aktualisiert</label>
                  <div className="ac-content-staticbox mt-2">{challenge.updatedAtLabel}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="ac-content-field mt-5">
            <label className="ac-content-label">Titel</label>
            <input
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              className="ac-content-input"
            />
          </div>

          <div className="ac-content-field mt-5">
            <label className="ac-content-label">Beschreibung</label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={8}
              className="ac-content-textarea"
            />
          </div>

          <div className="ac-content-actions-row">
            <button onClick={() => navigate("/content/challenges")} className="ac-ghostbtn ac-content-smallbtn">
              Zur Challenges Liste
            </button>

            <button
              onClick={handleSave}
              className="ac-primarybtn ac-content-smallbtn"
              disabled={saving}
            >
              {saving ? "Speichert..." : "Änderungen speichern"}
            </button>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}